import { Button, Center, Stack, Text, Title } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { useResetPassword } from '@/api/generated/registration/registration';
import { IconFigmaArrowRight, IconFigmaLock, IconFigmaShieldCheck } from '@/assets/icons';
import { FormErrorMessage, MutationButton, PasswordField } from '@/components/common';
import { AuthCard } from '@/features/auth/components/AuthCard';
import { brandTextStyle } from '@/features/auth/components/authFieldStyles';
import { InvalidLinkCard } from '@/features/auth/components/InvalidLinkCard';
import { isNetworkError, isTimeoutError } from '@/utils/apiError';

const schema = z
  .object({
    newPassword: z.string().min(8),
    confirmPassword: z.string().min(8)
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    message: 'passwordsMustMatch',
    path: ['confirmPassword']
  });

type FormValues = z.infer<typeof schema>;

export const ResetPasswordForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [passwordChanged, setPasswordChanged] = useState(false);

  const resetPasswordMutation = useResetPassword({
    mutation: {
      onSuccess: () => setPasswordChanged(true)
    }
  });

  const form = useForm<FormValues>({
    validate: zodResolver(schema),
    initialValues: { newPassword: '', confirmPassword: '' }
  });

  const handleSubmit = (values: FormValues) => {
    if (!token) return;
    resetPasswordMutation.mutate({ data: { token, newPassword: values.newPassword } });
  };

  // Timeout/network greška (ili isPaused — React Query uopće ne pokreće
  // zahtjev dok je navigator.onLine false, čeka da se veza vrati) ne znači
  // da je token nevažeći — poslužitelj uopće nije (još) odgovorio, pa
  // korisnik treba priliku da pokuša ponovno na ISTOM linku, ne da ga se
  // šalje po posve nov link.
  const isConnectivityError =
    resetPasswordMutation.isPaused ||
    (resetPasswordMutation.isError &&
      (isTimeoutError(resetPasswordMutation.error) || isNetworkError(resetPasswordMutation.error)));

  if (passwordChanged) {
    return (
      <AuthCard px={32} py={36} w={{ base: '100%', md: 420 }}>
        <Stack align="center" gap={14}>
          <Center
            w={56}
            h={56}
            style={{
              borderRadius: 12,
              backgroundColor: 'var(--color-accent-secondary-soft)',
              color: 'var(--color-icon-accent)'
            }}
          >
            <IconFigmaShieldCheck size={24} />
          </Center>
          <Title order={2} fz={24} fw={700} ta="center" style={brandTextStyle}>
            {t('resetPassword.successTitle')}
          </Title>
          <Text size="sm" ta="center" style={{ color: 'var(--color-text-secondary)' }}>
            {t('resetPassword.successMessage')}
          </Text>

          <Button
            fullWidth
            rightSection={<IconFigmaArrowRight size={15} />}
            onClick={() => navigate('/login')}
          >
            {t('resetPassword.successButton')}
          </Button>
        </Stack>
      </AuthCard>
    );
  }

  // Token fali u URL-u ILI ga je backend odbio kao nevažeći/istekao/već
  // iskorišten (401) — obje situacije znače isto: treba novi link.
  if (!token || (resetPasswordMutation.isError && !isConnectivityError)) {
    return (
      <InvalidLinkCard
        title={t('resetPassword.invalidTitle')}
        message={t('resetPassword.invalidMessage')}
        hint={t('resetPassword.invalidHint')}
        retryLabel={t('resetPassword.invalidRetry')}
        retryTo="/forgot-password"
        backTo="/login"
      />
    );
  }

  return (
    <AuthCard px={32} py={36} w={{ base: '100%', md: 440 }}>
      <Stack align="center" gap={14}>
        <Center
          w={56}
          h={56}
          style={{
            borderRadius: 12,
            backgroundColor: 'var(--color-accent-secondary-soft)',
            color: 'var(--color-icon-accent)'
          }}
        >
          <IconFigmaLock size={24} />
        </Center>
        <Title order={2} fz={24} fw={700} ta="center" style={brandTextStyle}>
          {t('resetPassword.title')}
        </Title>
        <Text size="sm" ta="center" style={{ color: 'var(--color-text-secondary)' }}>
          {t('resetPassword.subtitle')}
        </Text>

        {isConnectivityError && (
          <FormErrorMessage
            message={
              !resetPasswordMutation.isPaused && isTimeoutError(resetPasswordMutation.error)
                ? t('errors.timeout.message')
                : t('errors.network.message')
            }
          />
        )}

        <form onSubmit={form.onSubmit(handleSubmit)} style={{ width: '100%' }} noValidate>
          <Stack gap="md">
            <PasswordField
              label={t('resetPassword.newPassword')}
              required
              {...form.getInputProps('newPassword')}
            />
            <PasswordField
              label={t('resetPassword.confirmPassword')}
              required
              error={
                form.errors.confirmPassword ? t('resetPassword.passwordsMustMatch') : undefined
              }
              {...form.getInputProps('confirmPassword')}
            />

            <MutationButton
              type="submit"
              fullWidth
              isPending={resetPasswordMutation.isPending}
              isPaused={resetPasswordMutation.isPaused}
            >
              {t('resetPassword.submit')}
            </MutationButton>
          </Stack>
        </form>
      </Stack>
    </AuthCard>
  );
};
